import {
  toastUploadError,
  toastUploadResult,
} from 'Features/Projects/Table/uploadToasts'
import { Box } from '@mui/material'
import { BatchSetting } from 'Features/Projects/Scenarios/EditSettings/LineSettings/LineSettings.types'
import { GridColumnGroupingModel } from '@mui/x-data-grid-premium'
import { useMemo } from 'react'
import { BaseTable } from 'Features/Projects/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Projects/Table/BulkUpdateModal'
import { useSettingsContext } from 'Features/Projects/Scenarios/EditSettings/EditSettings.types'
import { toast } from 'react-hot-toast'
import { useUploadBatchSettings } from 'Features/Projects/Scenarios/EditSettings/LineSettings/LineSettings.data'
import {
  editsFromRows,
  useLinePreview,
  useSaveLineSettings,
} from '../lineRecalculation'
import {
  LinePreviewNote,
  toastLineSaved,
  toastLineSaveFailed,
} from '../LinePreviewNote'
import {
  cycleOverrideColumn,
  setByColumn,
  showProblems,
  useCycleGrid,
} from '../cycleColumns'

type BatchSettingsTableProps = {
  data: BatchSetting[]
  isLoading?: boolean
}

const OVERRIDES = ['minBatchCycleOverride', 'maxBatchCycleOverride']

const number = (digits: number) => (value: number | null) =>
  value === null || value === undefined ? '' : value.toFixed(digits)

const COLUMN_GROUPS: GridColumnGroupingModel = [
  {
    groupId: 'Demand',
    children: [
      { field: 'salesRate' },
      { field: 'minimumBatchSize' },
      { field: 'minimumBatchSizeIncrement' },
    ],
  },
  {
    groupId: 'From the rules',
    description: 'What the minimum batch size gives, before overrides',
    children: [{ field: 'naturalCycle' }, { field: 'initialBatchCycle' }],
  },
  {
    groupId: 'Overrides',
    description: 'Type 1, 2, 4 or 8 on a cell; Delete clears it',
    children: [
      { field: 'minBatchCycleOverride' },
      { field: 'maxBatchCycleOverride' },
    ],
  },
  {
    groupId: 'Result',
    description: 'What the template generator uses',
    children: [
      { field: 'minBatchCycleInput' },
      { field: 'maxBatchCycleInput' },
      { field: 'cycleSetBy' },
      { field: 'replenishmentTypeCode' },
      { field: 'isIncluded' },
    ],
  },
]

const INITIAL_STATE = {
  pagination: { paginationModel: { pageSize: 100 } },
  columns: { columnVisibilityModel: { isOverriden: false } },
}

/**
 * Batch production. Edits are previewed and saved through the API's cycle
 * rules (lineRecalculation.ts); a batch change also recalculates its SKU
 * groups, which the preview counts and SKU production then shows. Slowing a
 * batch past one of its SKU groups' max cycle is refused: the SKU group
 * would have no cycle left (the note names them).
 */
export const BatchSettingsTable = ({
  data,
  isLoading,
}: BatchSettingsTableProps) => {
  const { state } = useSettingsContext()
  const { projectId, scenarioId } = state
  const preview = useLinePreview({
    kind: 'batches',
    projectId,
    scenarioId,
    data,
  })
  const cycleGrid = useCycleGrid({
    kind: 'batches',
    data,
    overrideFields: OVERRIDES,
    previewApiRef: preview.apiRefCallback,
    problemsAt: preview.problemsAt,
  })
  const { mutate: save, isPending: isSaving } = useSaveLineSettings(
    projectId,
    scenarioId,
    toastLineSaved,
    (error) => {
      preview.saveFailed(error)
      toastLineSaveFailed(error)
    }
  )

  const problems = cycleGrid.problems
  const batchSettingColumns: ExtendedGridColDef[] = useMemo(() => {
    return [
      {
        field: 'baseSourceSiteCode',
        headerName: 'Site',
        width: 96,
        type: 'string',
      },
      {
        field: 'workCentreCode',
        headerName: 'Work centre',
        width: 112,
        type: 'string',
      },
      {
        field: 'batchGroupingCode',
        headerName: 'Batch group',
        width: 120,
        type: 'string',
      },
      {
        field: 'unitOfMeasureCode',
        headerName: 'Unit of measure',
        width: 120,
        type: 'string',
      },
      {
        field: 'salesRate',
        headerName: 'Sales rate',
        description:
          "Weekly demand on this line: its SKU groups' allocated sales, in the batch unit",
        width: 104,
        type: 'number',
        valueFormatter: number(2),
      },
      showProblems(
        {
          field: 'minimumBatchSize',
          headerName: 'Min batch size',
          width: 120,
          type: 'number',
          editable: true,
        },
        problems
      ),
      {
        field: 'minimumBatchSizeIncrement',
        headerName: 'Increment',
        width: 96,
        type: 'number',
      },
      {
        field: 'naturalCycle',
        headerName: 'Natural',
        description:
          'Weeks one minimum batch lasts: min batch size ÷ sales rate',
        width: 88,
        type: 'number',
        valueFormatter: number(2),
      },
      {
        field: 'initialBatchCycle',
        headerName: 'Initial',
        description: 'The natural cycle rounded up to 1, 2, 4 or 8',
        width: 80,
        type: 'number',
      },
      cycleOverrideColumn({
        field: 'minBatchCycleOverride',
        headerName: 'Min override',
        description:
          "Run no more often than this. Can't be slower than a SKU group's max cycle.",
        bound: 'min',
        minInput: 'minBatchCycleInput',
        maxInput: 'maxBatchCycleInput',
        problems,
      }),
      cycleOverrideColumn({
        field: 'maxBatchCycleOverride',
        headerName: 'Max override',
        description:
          'Run at least this often. Type 1, 2, 4 or 8; Delete clears it.',
        bound: 'max',
        minInput: 'minBatchCycleInput',
        maxInput: 'maxBatchCycleInput',
        problems,
      }),
      {
        field: 'minBatchCycleInput',
        headerName: 'Min cycle',
        width: 96,
        type: 'number',
      },
      {
        field: 'maxBatchCycleInput',
        headerName: 'Max cycle',
        width: 96,
        type: 'number',
      },
      setByColumn(),
      {
        field: 'replenishmentTypeCode',
        headerName: 'Type',
        width: 80,
        type: 'string',
      },
      {
        field: 'isIncluded',
        headerName: 'Included',
        width: 88,
        type: 'boolean',
      },
      {
        field: 'isOverriden',
        headerName: 'Is overridden',
        description: 'Min cycle differs from the initial cycle',
        width: 112,
        type: 'boolean',
      },
    ]
  }, [problems])

  const updateTableData = (changedRows: Record<number, BatchSetting>) => {
    const before = new Map(data.map((row) => [row.id, row]))
    const batches = editsFromRows(
      'batches',
      Object.values(changedRows).map((current) => ({
        current,
        before: before.get(current.id),
      }))
    )
    if (!batches.length) {
      toast('No input changes to save')
      return
    }
    preview.holdForSave()
    save({ batches })
  }

  const { mutate: uploadMutate, isPending: isUploading } =
    useUploadBatchSettings(toastUploadResult, toastUploadError)

  const uploadData = (file: File) => {
    uploadMutate({ file, projectId, scenarioId })
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        overflow: 'auto',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ px: 2, pb: 2 }}>{cycleGrid.quickFilters}</Box>
      <LinePreviewNote
        summary={preview.summary}
        isPreviewing={preview.isPreviewing}
        problems={preview.problems}
      />
      <BaseTable
        data={data}
        columns={batchSettingColumns}
        isLoading={isLoading ?? false}
        updateTableData={updateTableData}
        onPendingChange={preview.onPendingChange}
        getCellClassName={preview.getCellClassName}
        apiRefCallback={cycleGrid.apiRefCallback}
        onCellKeyDown={cycleGrid.onCellKeyDown}
        onFilterModelChange={cycleGrid.onFilterModelChange}
        saveBlockedReason={preview.saveBlockedReason}
        cellSelection
        columnGroupingModel={COLUMN_GROUPS}
        initialState={INITIAL_STATE}
        isSaving={isSaving}
        checkBoxSelection={true}
        pinnedColumns={[
          'baseSourceSiteCode',
          'workCentreCode',
          'batchGroupingCode',
        ]}
        uploadData={uploadData}
        isUploading={isUploading ?? false}
        customDownloadDataMethod={(tableData) => tableData}
      />
    </Box>
  )
}
